import React from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Package, ShoppingCart, TrendingUp, Layers } from 'lucide-react';

const Dashboard = () => {
  const { products = [], orders = [], updateOrderStatus } = useAdminAuth();

  const totalProducts = products.length;
  const totalOrders = orders.length;

  const revenue = orders
    .filter((o) => {
      const st = (o.status || '').toUpperCase();
      return st === 'DELIVERED' || st === 'CONFIRMED';
    })
    .reduce((sum, o) => sum + Number(o.totalAmount || o.total || 0), 0);

  const outOfStock = products.filter(
    (p) => Number(p.stockQuantity ?? p.stock ?? 0) === 0
  ).length;

  const stats = [
    { label: 'Ümumi Məhsul', value: totalProducts, icon: Package, color: 'bg-blue-600' },
    { label: 'Ümumi Sifariş', value: totalOrders, icon: ShoppingCart, color: 'bg-green-600' },
    { label: 'Ümumi Gəlir', value: `${revenue.toFixed(2)} ₼`, icon: TrendingUp, color: 'bg-purple-600' },
    { label: 'Bitən Məhsullar', value: `${outOfStock} ədəd`, icon: Layers, color: 'bg-amber-600' },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('az-AZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const statusLabel = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s === 'PENDING') return 'Gözləmədə';
    if (s === 'CONFIRMED') return 'Təsdiqli';
    if (s === 'CANCELLED') return 'Ləğv edilib';
    return 'Çatdırıldı';
  };

  const statusClass = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s === 'PENDING') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (s === 'CONFIRMED') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s === 'CANCELLED') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };

  const StatusSelect = ({ order, className = '' }) => (
    <select
      value={(order.status || 'PENDING').toUpperCase()}
      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
      className={`bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-black cursor-pointer font-medium ${className}`}
    >
      <option value="PENDING">Gözləmədə</option>
      <option value="CONFIRMED">Təsdiqlə</option>
      <option value="DELIVERED">Çatdırıldı</option>
      <option value="CANCELLED">Ləğv et</option>
    </select>
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif text-gray-900">Panelə Baxış</h1>
        <p className="text-sm text-gray-500 mt-1">
          L'Aliel butikinin cari aktivlik göstəriciləri.
        </p>
      </div>

      {/* Statistika — mobildə 2 sütun */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-start sm:items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <p className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wider font-semibold leading-tight">
                  {stat.label}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1.5 sm:mt-2 break-words">
                  {stat.value}
                </p>
              </div>
              <div className={`${stat.color} p-2.5 sm:p-3.5 rounded-xl text-white flex-shrink-0`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Sifarişlər */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-serif font-bold text-gray-900">
            Bütün Sifarişlər
          </h2>
          <span className="text-xs bg-gray-100 text-gray-600 font-medium px-2.5 py-1 rounded-full flex-shrink-0">
            Canlı
          </span>
        </div>

        {orders.length === 0 ? (
          <p className="p-6 text-center text-gray-400 italic text-sm">
            Hələ heç bir sifariş yoxdur.
          </p>
        ) : (
          <>
            {/* ---------- MOBİL: kart görünüşü ---------- */}
            <div className="lg:hidden divide-y divide-gray-100">
              {orders.map((order) => {
                const customer = order.customerName || order.customer || 'Anonim';
                const amount = Number(order.totalAmount ?? order.total ?? 0).toFixed(2);

                return (
                  <div key={order.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{customer}</p>
                        <p className="font-mono text-xs text-gray-500 mt-0.5">
                          #{order.id || order.orderNumber}
                        </p>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border flex-shrink-0 ${statusClass(
                          order.status
                        )}`}
                      >
                        {statusLabel(order.status)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 text-xs">
                        {formatDate(order.createdAt || order.date)}
                      </span>
                      <span className="font-semibold text-gray-950">
                        {amount} {order.currency || '₼'}
                      </span>
                    </div>

                    <StatusSelect order={order} className="w-full text-sm" />
                  </div>
                );
              })}
            </div>

            {/* ---------- DESKTOP: cədvəl ---------- */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase border-b border-gray-100">
                    <th className="p-4 pl-6">Sifariş ID</th>
                    <th className="p-4">Müştəri</th>
                    <th className="p-4">Tarix</th>
                    <th className="p-4">Məbləğ</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-center">Əməliyyat</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-sm text-gray-700">
                  {orders.map((order) => {
                    const customer = order.customerName || order.customer || 'Anonim';
                    const amount = Number(order.totalAmount ?? order.total ?? 0).toFixed(2);

                    return (
                      <tr key={order.id} className="hover:bg-gray-50/70 transition-colors">
                        <td className="p-4 pl-6 font-mono text-xs text-gray-900 font-semibold">
                          #{order.id || order.orderNumber}
                        </td>
                        <td className="p-4 font-medium">{customer}</td>
                        <td className="p-4 text-gray-500 text-xs">
                          {formatDate(order.createdAt || order.date)}
                        </td>
                        <td className="p-4 font-semibold text-gray-950">
                          {amount} {order.currency || '₼'}
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium inline-block border ${statusClass(
                              order.status
                            )}`}
                          >
                            {statusLabel(order.status)}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <StatusSelect order={order} className="text-xs shadow-sm" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
